import { useState, useEffect , useCallback} from 'react';
import { User } from 'firebase/auth';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Mail, 
  UserPlus, 
  X, 
  AlertCircle, 
  CheckCircle,
  Users,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { checkEmailAccess } from '@/lib/access-control';
import type { AccessControl } from '@/lib/firebase';
import { QuickSubscribeButton } from './QuickSubscribeButton';

interface EmailSubscriptionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  assistantId: string;
  assistantName: string;
  currentUser: User | null;
  accessControl?: AccessControl;
  ownerEmail?: string;
  emailSubscriptions?: string[];
}

interface Subscription {
  email: string;
  subscribedAt?: string;
}

export function EmailSubscriptionDialog({
  isOpen,
  onClose,
  assistantId,
  assistantName,
  currentUser,
  accessControl,
  ownerEmail,
  emailSubscriptions
}: EmailSubscriptionDialogProps) {
  const [emails, setEmails] = useState<string[]>(['']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [existingSubscriptions, setExistingSubscriptions] = useState<Subscription[]>([]);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);

  // Load existing subscriptions when dialog opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadExistingSubscriptions();
    }
  }, [isOpen, currentUser]);

  const loadExistingSubscriptions = useCallback(async () => {
    setLoadingSubscriptions(true);
    try {
      // Use emailSubscriptions prop if available
      if (emailSubscriptions) {
        const subs = emailSubscriptions.map(email => ({
          email,
          subscribedAt: undefined // We don't have individual subscription dates
        }));
        setExistingSubscriptions(subs);
      } else {
        // Fallback to API call if needed
        if (!currentUser?.email) return;
        
        const response = await fetch('/api/proxy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            endpoint: `/vac/subscriptions/user/${encodeURIComponent(currentUser.email)}`,
            method: 'GET'
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Filter subscriptions for this assistant
          const assistantSubs = data.subscriptions
            .filter((sub: any) => sub.assistant_id === assistantId)
            .map((sub: any) => ({
              email: sub.subscriber_email,
              subscribedAt: sub.subscribed_at
            }));
          setExistingSubscriptions(assistantSubs);
        }
      }
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setLoadingSubscriptions(false);
    }
  }, [assistantId, currentUser, emailSubscriptions]);

  const handleAddEmail = () => {
    setEmails([...emails, '']);
  };

  const handleRemoveEmail = (index: number) => {
    // Always keep at least one input field
    if (emails.length <= 1) {
      setEmails(['']);
    } else {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const validateEmails = (): string[] => {
    const validEmails: string[] = [];
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    for (const email of emails) {
      const trimmed = email.trim();
      if (trimmed && emailRegex.test(trimmed)) {
        validEmails.push(trimmed);
      }
    }
    
    return [...new Set(validEmails)]; // Remove duplicates
  };

  // Function to get access validation for a specific email
  const getEmailValidation = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) return null;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      return { hasAccess: false, reason: 'Invalid email format' };
    }
    
    return checkEmailAccess(trimmed, accessControl, ownerEmail);
  };

  const handleSubscribe = async () => {
    if (!currentUser?.email) {
      setError('You must be logged in to manage subscriptions');
      return;
    }

    const validEmails = validateEmails();
    if (validEmails.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: `/vac/subscriptions/${assistantId}`,
          method: 'POST',
          subscriber_emails: validEmails,
          requestor_email: currentUser.email
        })
      });

      const data = await response.json();

      if (response.ok) {
        const { total_subscribed, invalid_subscribers, valid_subscribers } = data;
        
        if (total_subscribed > 0) {
          let message = `Successfully subscribed ${total_subscribed} email(s) to ${assistantName}`;
          if (invalid_subscribers && invalid_subscribers.length > 0) {
            message += `\n\nNote: ${invalid_subscribers.length} email(s) were rejected due to access restrictions: ${invalid_subscribers.join(', ')}`;
          }
          setSuccess(message);
        } else if (invalid_subscribers && invalid_subscribers.length > 0) {
          setError(`All emails were rejected due to access restrictions. Only users with access to this assistant can subscribe to notifications.\n\nRejected emails: ${invalid_subscribers.join(', ')}`);
        } else {
          setError('No emails were subscribed. Please check the email addresses and try again.');
        }
        
        // Reset form only if some emails were successful
        if (total_subscribed > 0) {
          setEmails(['']);
        }
        
        // Reload subscriptions
        loadExistingSubscriptions();
      } else {
        setError(data.error || 'Failed to subscribe emails');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Subscription error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (email: string) => {
    if (!currentUser?.email) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          endpoint: `/vac/subscriptions/${assistantId}`,
          method: 'DELETE',
          subscriber_email: email
        })
      });

      if (response.ok) {
        setSuccess(`Successfully unsubscribed ${email}`);
        // Reload subscriptions
        loadExistingSubscriptions();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to unsubscribe');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Unsubscribe error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Email Subscriptions for {assistantName}
          </DialogTitle>
          <DialogDescription>
            Subscribe email addresses to receive notifications when anyone interacts with this assistant.
            Subscribers will be notified of all conversations regardless of how they were initiated (web, email, API, etc.).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Quick Subscribe/Unsubscribe for current user */}
          {currentUser && (
            <div className="p-3 bg-muted/30 rounded-md border">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Quick Action</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Subscribe or unsubscribe yourself ({currentUser.email})
                  </p>
                </div>
                <QuickSubscribeButton
                  assistantId={assistantId}
                  assistantName={assistantName}
                  currentUser={currentUser}
                  emailSubscriptions={emailSubscriptions}
                  variant="default"
                  onSubscriptionChange={() => {
                    // Reload subscriptions after change
                    loadExistingSubscriptions();
                  }}
                />
              </div>
            </div>
          )}

          {/* Existing Subscriptions */}
          {existingSubscriptions.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Current Subscribers ({existingSubscriptions.length})
              </Label>
              <ScrollArea className="h-32 w-full rounded-md border p-2">
                <div className="space-y-1">
                  {existingSubscriptions.map((sub, index) => (
                    <div key={index} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3 text-muted-foreground" />
                        <span className="text-sm">{sub.email}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnsubscribe(sub.email)}
                        disabled={loading}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Add New Subscriptions */}
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Add Email Subscribers
            </Label>
            <div className="space-y-3">
              {emails.map((email, index) => {
                const validation = getEmailValidation(email);
                const hasContent = email.trim().length > 0;
                
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input
                          type="email"
                          placeholder={`Enter email address ${emails.length > 1 ? `#${index + 1}` : ''}`}
                          value={email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          disabled={loading}
                          className={
                            !hasContent ? "" :
                            validation?.hasAccess ? "border-green-200 bg-green-50" :
                            validation?.hasAccess === false ? "border-red-200 bg-red-50" :
                            "border-blue-200 bg-blue-50"
                          }
                        />
                        {hasContent && validation && !validation.hasAccess && validation.reason && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                            <AlertTriangle className="h-3 w-3" />
                            <span>{validation.reason}</span>
                          </div>
                        )}
                        {hasContent && validation?.hasAccess && (
                          <div className="flex items-center gap-1 mt-1 text-xs text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            <span>Email will be accepted</span>
                          </div>
                        )}
                      </div>
                      {emails.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveEmail(index)}
                          disabled={loading}
                          title={`Remove email input ${index + 1}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddEmail}
              className="mt-2"
              disabled={loading}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add Another Email
            </Button>
          </div>

          {/* Status Messages */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">{success}</AlertDescription>
            </Alert>
          )}

          {/* Email Info */}
          <Alert>
            <Mail className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium mb-1">Email this assistant at:</p>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                assistant-{assistantId}@email.aitana.chat
              </code>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubscribe} 
            disabled={loading || validateEmails().length === 0}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 mr-2" />
                Subscribe {validateEmails().length > 0 ? `${validateEmails().length} Email${validateEmails().length > 1 ? 's' : ''}` : 'Emails'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}